/**
 * O shell do aplicativo (UX-DR1, UX-DR2, NFR-1).
 *
 * No computador: sidebar escura de 214px com o logo, os itens do perfil, o rodape do usuario
 * e "Sair"; a direita, o cabecalho de tela e o `<Outlet/>`.
 *
 * No celular (ate 768px): a sidebar some, o cabecalho vira a barra navy com o titulo e as
 * iniciais, e a navegacao desce para a barra inferior de cinco abas do prototipo — mais uma
 * sexta, "Mais", que abre a folha com **todo** item que nao coube, o bloco do usuario e
 * "Sair". A NFR-1 nao aceita item alcancavel so no computador, e o prototipo mobile deixa
 * Empresas e Administracao de fora; a folha e o que concilia os dois sem mexer nas cinco.
 *
 * **Um DOM de cada vez.** O layout de computador e o de celular nao coexistem escondidos por
 * CSS: `useEhCelular` escolhe qual renderizar. Dois DOMs simultaneos dariam dois links
 * "Empresas" na mesma pagina — um invisivel — e todo teste de acessibilidade e de ponta a
 * ponta teria de desempatar entre eles.
 */

import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import {
  type MouseEvent,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'

import { createPortal } from 'react-dom'

import logoDaOrvalho from '@/assets/orvalho-logo.png'
import { TETO_DO_CONTADOR, useTotalDeNaoLidas } from '@/modulos/comunicacao/api'
import { useSair, useSessao } from '@/modulos/identidade/sessao'
import { iniciaisDoNome } from './iniciais'
import {
  ABA_MAIS,
  type ItemDeNavegacao,
  LARGURA_MAXIMA_DO_CELULAR,
  itemDoCaminho,
  itensDaBarraInferior,
  itensDaFolha,
  itensPara,
} from './navegacao'
import { iniciarAConferencia, recarregarSemCache, useVersaoNova } from './versao-nova'
import './shell.css'

const CONSULTA_DE_CELULAR = `(max-width: ${String(LARGURA_MAXIMA_DO_CELULAR)}px)`

/**
 * Se a tela e de celular, acompanhando a mudanca de tamanho.
 *
 * `useSyncExternalStore` e nao `useState` + `useEffect`: a primeira renderizacao ja precisa do
 * valor certo, senao o shell pisca o layout de computador antes de trocar — e um teste de
 * viewport de iPhone flagraria a sidebar por um quadro.
 */
export function useEhCelular(): boolean {
  const assinar = useCallback((avisar: () => void) => {
    const consulta = window.matchMedia(CONSULTA_DE_CELULAR)
    consulta.addEventListener('change', avisar)
    return () => {
      consulta.removeEventListener('change', avisar)
    }
  }, [])

  return useSyncExternalStore(
    assinar,
    () => window.matchMedia(CONSULTA_DE_CELULAR).matches,
    () => false,
  )
}

const CONSULTA_DE_TOQUE = '(any-pointer: coarse)'

/**
 * Se a tela tem toque — algum ponteiro grosso (13.14c, DW-426) —, acompanhando a mudanca (o mouse
 * ligado no tablet, o teclado que solta do notebook).
 *
 * `any-pointer`, e nao `pointer`: o notebook com tela de toque diz `pointer: fine` (o mouse e o
 * primario) e ainda e tocado; o "⋯" a mais nao atrapalha quem usa o mouse. E o caso que o Safari do
 * iPad pede: ele nao dispara `contextmenu` no toque longo, e a partir de 768 px a tela e a do
 * computador.
 */
export function useToqueNaTela(): boolean {
  const assinar = useCallback((avisar: () => void) => {
    const consulta = window.matchMedia(CONSULTA_DE_TOQUE)
    consulta.addEventListener('change', avisar)
    return () => {
      consulta.removeEventListener('change', avisar)
    }
  }, [])

  return useSyncExternalStore(
    assinar,
    () => window.matchMedia(CONSULTA_DE_TOQUE).matches,
    () => false,
  )
}

/** Os itens do perfil de quem esta logado e o item da rota aberta. */
export function useNavegacaoAtual() {
  const { data: sessao } = useSessao()
  const caminho = useRouterState({ select: (estado) => estado.location.pathname })
  const itens = sessao ? itensPara(sessao.perfil, sessao.escopo_de_carteira, sessao.permissoes) : []
  return { sessao, caminho, itens, itemAtual: itemDoCaminho(itens, caminho) }
}

/**
 * Por onde uma tela empresta o subtitulo do cabecalho do shell.
 *
 * O `Shell` desenha o cabecalho, e a tela e quem tem o dado: a contagem de "21.460 empresas" so
 * existe depois de `GET /empresas` responder, e depende de **quem** esta
 * lendo (cada perfil enxerga uma base diferente). O item de navegacao nao tem como carregar
 * isso — ele e uma constante de modulo.
 *
 * O valor do contexto e o **setter** do estado do `Shell`, e nao um objeto: a identidade de um
 * setter de `useState` e estavel, entao o efeito do hook abaixo nao dispara a cada render.
 *
 * `null` fora do `Shell` e proposital: o hook vira um no-op em vez de estourar, e uma secao
 * que amanha seja montada fora do shell (um teste de unidade, uma pagina publica) nao quebra.
 */
const ContextoDoSubtituloDaTela = createContext<
  ((subtitulo: string | undefined) => void) | null
>(null)

/**
 * Define o subtitulo do cabecalho enquanto esta tela estiver montada.
 *
 * A limpeza e o que importa tanto quanto a definicao: sem ela, sair de `/empresas` deixaria a
 * contagem de Empresas no cabecalho de Tarefas. Passar `undefined` devolve o subtitulo ao do
 * item de navegacao — que e o do prototipo, quando existe.
 */
export function useDefinirSubtitulo(subtitulo: string | undefined): void {
  const definir = useContext(ContextoDoSubtituloDaTela)

  useEffect(() => {
    definir?.(subtitulo)
    return () => {
      definir?.(undefined)
    }
  }, [definir, subtitulo])
}

/**
 * Por onde uma tela empresta o **titulo** do cabecalho do shell.
 *
 * Ele e o irmao exato de `ContextoDoSubtituloDaTela`, e nasceu pela mesma razao, um degrau
 * acima: o `Shell` desenha o cabecalho a partir do **item de navegacao** da rota aberta, e uma
 * tela que nao e item de menu — a ficha do Negocio (3.10), a ficha da Empresa — nao tem item
 * nenhum. Sem este par, `itemAtual` e `undefined` e o `<h1>` cai no fallback
 * `'CRM Orvalho do Sol'`: a ficha do Negocio chegaria sob o nome do produto.
 *
 * O valor do contexto e o **setter** do estado do `Shell`, e nao um objeto: a identidade de um
 * setter de `useState` e estavel, entao o efeito do hook abaixo nao dispara a cada render.
 *
 * `null` fora do `Shell` e proposital: o hook vira um no-op em vez de estourar, e uma secao
 * que amanha seja montada fora do shell (um teste de unidade, uma pagina publica) nao quebra.
 */
const ContextoDoTituloDaTela = createContext<((titulo: string | undefined) => void) | null>(null)

/**
 * Define o titulo do cabecalho enquanto esta tela estiver montada.
 *
 * A limpeza e o que importa tanto quanto a definicao, e aqui ela importa **mais** que no
 * subtitulo: sem ela, sair da ficha do Negocio deixaria "Negocio" no cabecalho de Tarefas — e o
 * titulo, ao contrario do subtitulo, e o unico `<h1>` da pagina. Passar `undefined` devolve o
 * titulo ao do item de navegacao, ou ao fallback quando nao ha item.
 */
export function useDefinirTitulo(titulo: string | undefined): void {
  const definir = useContext(ContextoDoTituloDaTela)

  useEffect(() => {
    definir?.(titulo)
    return () => {
      definir?.(undefined)
    }
  }, [definir, titulo])
}

/**
 * Por onde uma tela põe **ações** no cabeçalho do shell — a busca e o `+ Negócio` do Funil, no
 * desenho do protótipo v2 (`.cabecalho__acoes`: busca à esquerda, ação primária à direita).
 *
 * Não é um terceiro setter de texto: ação é árvore React, e um nó novo a cada render num
 * `useEffect` faria o `Shell` re-renderizar a tela, que produziria outro nó, sem fim. O valor do
 * contexto é o **elemento** do slot (o `<div>` do cabeçalho, preenchido por callback ref), e a
 * tela desenha nele por portal — `createPortal` não passa pelo estado de ninguém.
 *
 * `null` antes do primeiro commit e fora do `Shell`: `AcoesDoCabecalho` desenha nada até o slot
 * existir, e uma tela montada sem shell (um teste de unidade) não quebra.
 */
const ContextoDoSlotDeAcoes = createContext<HTMLElement | null>(null)

/**
 * As ações do cabeçalho da tela aberta.
 *
 * No computador elas vão para o slot do cabeçalho do shell (portal). No celular o cabeçalho tem
 * 46 px e o menu de iniciais — não há lugar —, e as ações ficam **na tela**, no topo do corpo, para
 * que nada que o computador oferece se perca (NFR-1).
 */
export function AcoesDoCabecalho({ children }: { children: ReactNode }) {
  const slot = useContext(ContextoDoSlotDeAcoes)
  const ehCelular = useEhCelular()
  /* Sem slot (celular, ou tela montada fora do shell) as ações ficam na própria tela: nenhuma
     ação some por falta de cabeçalho. */
  if (ehCelular || slot === null) {
    return <div className="shell__acoes-na-tela">{children}</div>
  }
  return createPortal(children, slot)
}

/**
 * Os dois contextos que a tela aberta usa para emprestar texto ao cabecalho, num componente so.
 *
 * Ele existe para que as duas arvores do `Shell` — computador e celular — abram **um** elemento,
 * e nao dois aninhados: a alternativa era repetir o par de `Provider` nos dois lugares, e o dia
 * em que nascesse um terceiro contexto de cabecalho os dois teriam de ser editados em
 * concordancia. Aqui ele nasce uma vez.
 *
 * Os valores sao os **setters** do estado do `Shell`, e nao objetos: a identidade de um setter de
 * `useState` e estavel, entao os efeitos de `useDefinirSubtitulo` e `useDefinirTitulo` nao
 * disparam a cada render.
 */
function ContextosDoCabecalho({
  subtitulo,
  titulo,
  slotDeAcoes,
  children,
}: {
  subtitulo: (subtitulo: string | undefined) => void
  titulo: (titulo: string | undefined) => void
  /** O elemento do slot de ações do cabeçalho — `null` no celular, que não tem slot. */
  slotDeAcoes: HTMLElement | null
  children: ReactNode
}) {
  return (
    <ContextoDoSubtituloDaTela.Provider value={subtitulo}>
      <ContextoDoTituloDaTela.Provider value={titulo}>
        <ContextoDoSlotDeAcoes.Provider value={slotDeAcoes}>{children}</ContextoDoSlotDeAcoes.Provider>
      </ContextoDoTituloDaTela.Provider>
    </ContextoDoSubtituloDaTela.Provider>
  )
}

function Avatar({ nome }: { nome: string }) {
  return (
    <div className="shell__avatar" aria-hidden="true">
      {iniciaisDoNome(nome)}
    </div>
  )
}

/** A permissao que abre o WhatsApp — e o total de nao lidas do menu (13.8). */
const PERMISSAO_DO_WHATSAPP = 'acessar_whatsapp'

/** Acima disto o contador do menu escreve "99+" (13.8, D-10) — o mesmo teto da linha. */
const TETO_DO_CONTADOR_DO_MENU = TETO_DO_CONTADOR

/**
 * O contador de um item, quando ha (13.8): o numero desenhado e o que o leitor de tela ouve.
 *
 * **O numero e `aria-hidden`, e quem o diz e a descricao do link** (`aria-describedby`): dentro do
 * nome, "WhatsApp" viraria "WhatsApp 3" e mudaria a cada mensagem — e o nome do link e o que o
 * leitor de tela anuncia e o que `e2e/shell.spec.ts` procura.
 */
function contadorDo(quantas: number | undefined): { texto: string; descricao: string } | null {
  if (quantas === undefined || quantas <= 0) {
    return null
  }
  if (quantas > TETO_DO_CONTADOR_DO_MENU) {
    return {
      texto: `${String(TETO_DO_CONTADOR_DO_MENU)}+`,
      descricao: `mais de ${String(TETO_DO_CONTADOR_DO_MENU)} não lidas`,
    }
  }
  return {
    texto: String(quantas),
    descricao: quantas === 1 ? '1 não lida' : `${String(quantas)} não lidas`,
  }
}

/** O numero e a descricao escondida — os dois filhos que o link ganha quando ha contador. */
function Contador({ id, contador }: { id: string; contador: { texto: string; descricao: string } }) {
  return (
    <>
      <span className="shell__contador numero" aria-hidden="true">
        {contador.texto}
      </span>
      {/* Escondida das duas formas: `hidden` (fora da tela) e `aria-hidden` (fora do nome do
          link). A descricao a alcanca pelo `aria-describedby`, que le no oculto de proposito. */}
      <span id={id} hidden aria-hidden="true">
        {contador.descricao}
      </span>
    </>
  )
}

function ItemDaSidebar({
  item,
  ativo,
  quantas,
}: {
  item: ItemDeNavegacao
  ativo: boolean
  /** O contador do item (13.8: o WhatsApp), quando ha. */
  quantas?: number | undefined
}) {
  const contador = contadorDo(quantas)
  const idDoContador = `contador-${item.chave}`
  return (
    <Link
      to={item.caminho}
      className="shell__item"
      data-ativo={ativo ? 'sim' : 'nao'}
      aria-current={ativo ? 'page' : undefined}
      aria-describedby={contador === null ? undefined : idDoContador}
    >
      <span>{item.rotulo}</span>
      {/* O slot da badge (UX-DR2) existe; a contagem so chega com o servico que a produz. */}
      {item.badge === undefined ? null : (
        <span className="shell__badge numero">{item.badge}</span>
      )}
      {contador === null ? null : <Contador id={idDoContador} contador={contador} />}
    </Link>
  )
}

function BlocoDoUsuario({ nome, perfilRotulo }: { nome: string; perfilRotulo: string }) {
  return (
    <div className="shell__usuario">
      <Avatar nome={nome} />
      <div className="shell__usuario-texto">
        <span className="shell__usuario-nome">{nome}</span>
        <span className="shell__usuario-perfil">{perfilRotulo}</span>
      </div>
    </div>
  )
}

/**
 * O subtitulo que vale agora: o da **tela** vence o do item de navegacao.
 *
 * O do item e o literal do prototipo, escrito quando o dado ainda nao existia ("seus
 * negocios", "seu numero · ..."); o da tela e o dado de verdade, e so ele sabe da sessao e da
 * resposta do servico. Enquanto nenhuma tela definir o seu, nada muda.
 *
 * Ela e chamada **uma vez**, no `Shell`, e as duas arvores recebem o texto ja resolvido: com
 * uma segunda chamada dentro do `Cabecalho`, a regra de precedencia teria dois donos e um
 * deles ficaria morto — que e como ela nasceu, e o que este comentario existe para impedir.
 */
function subtituloVigente(
  item: ItemDeNavegacao | undefined,
  daTela: string | undefined,
): string | undefined {
  return daTela ?? item?.subtitulo
}

/**
 * O titulo que vale agora: o da **tela** vence o do item de navegacao.
 *
 * Ela e simetrica a `subtituloVigente`, e a precedencia e a mesma pela mesma razao: o do item e
 * o literal do prototipo, e o da tela e o que so ela sabe. A diferenca esta no terceiro caso, e
 * e ele que motivou o par: quando **nao ha item** — a rota nao e de menu —, sem o titulo da tela
 * o cabecalho cai no nome do produto.
 *
 * Ela e chamada **uma vez**, no `Shell`, e as duas arvores recebem o texto ja resolvido: uma
 * segunda chamada dentro do `Cabecalho` daria dois donos a regra de precedencia, e um deles
 * ficaria morto.
 */
function tituloVigente(item: ItemDeNavegacao | undefined, daTela: string | undefined): string {
  return daTela ?? item?.titulo ?? 'CRM Orvalho do Sol'
}

function Cabecalho({
  titulo,
  subtitulo,
  refDoSlotDeAcoes,
}: {
  /** Já resolvido por `tituloVigente` no `Shell`: aqui não há regra de precedência. */
  titulo: string
  /** Já resolvido por `subtituloVigente` no `Shell`: aqui não há regra de precedência. */
  subtitulo: string | undefined
  /** Callback ref do slot de ações: é por ele que a tela aberta chega ao cabeçalho. */
  refDoSlotDeAcoes: (elemento: HTMLDivElement | null) => void
}) {
  return (
    <header className="shell__cabecalho">
      {/* O par titulo + subtitulo vive num bloco proprio, alinhado pela **linha de base** — e
          o que faz o subtitulo de 12px assentar na mesma linha do titulo de 16px. */}
      <div className="shell__cabecalho-texto">
        <h1 className="shell__titulo">{titulo}</h1>
        {subtitulo === undefined ? null : <span className="shell__subtitulo">{subtitulo}</span>}
      </div>
      {/* As ações da tela (protótipo v2, `.cabecalho__acoes`): a tela as põe aqui por portal. */}
      <div className="shell__cabecalho-acoes" ref={refDoSlotDeAcoes} />
    </header>
  )
}

/**
 * A tela de qualquer item de navegacao ainda sem conteudo.
 *
 * O titulo e o subtitulo ja saem no cabecalho do shell; aqui fica a unica coisa que a pessoa
 * precisa saber ao chegar: de qual epico o conteudo vem. Um "em construcao" mudo obrigaria
 * quem abriu a perguntar.
 */
export function TelaPlaceholder() {
  const { itemAtual } = useNavegacaoAtual()
  return (
    <section className="shell__placeholder">
      <p>{itemAtual?.origemDoConteudo ?? 'Esta tela chega numa história futura.'}</p>
    </section>
  )
}

function Sidebar({
  itens,
  caminho,
  nome,
  perfilRotulo,
  aoSair,
  saindo,
  contadores,
}: {
  itens: ItemDeNavegacao[]
  caminho: string
  nome: string
  perfilRotulo: string
  aoSair: () => void
  saindo: boolean
  /** Os contadores por `chave` de item (13.8: so o WhatsApp). */
  contadores: Partial<Record<string, number>>
}) {
  return (
    <aside className="shell__sidebar">
      <div className="shell__logo">
        <img src={logoDaOrvalho} alt="Orvalho do Sol" />
      </div>
      <nav className="shell__nav" aria-label="Navegação principal">
        {itens.map((item) => (
          <ItemDaSidebar
            key={item.chave}
            item={item}
            ativo={item.caminho === caminho}
            quantas={contadores[item.chave]}
          />
        ))}
      </nav>
      <div className="shell__rodape">
        <BlocoDoUsuario nome={nome} perfilRotulo={perfilRotulo} />
        <button type="button" className="shell__sair" onClick={aoSair} disabled={saindo}>
          Sair
        </button>
      </div>
    </aside>
  )
}

function BarraInferior({
  itens,
  caminho,
  folhaAberta,
  aoAlternarFolha,
  contadores,
}: {
  itens: ItemDeNavegacao[]
  caminho: string
  folhaAberta: boolean
  aoAlternarFolha: (evento: MouseEvent<HTMLButtonElement>) => void
  /** Os contadores por `chave` de item (13.8: so o WhatsApp). */
  contadores: Partial<Record<string, number>>
}) {
  return (
    <nav className="shell__abas" aria-label="Navegação principal">
      {itens.map((item) => {
        const ativo = item.caminho === caminho
        const contador = contadorDo(contadores[item.chave])
        const idDoContador = `contador-${item.chave}`
        return (
          <Link
            key={item.chave}
            to={item.caminho}
            className="shell__aba"
            data-ativo={ativo ? 'sim' : 'nao'}
            aria-current={ativo ? 'page' : undefined}
            aria-describedby={contador === null ? undefined : idDoContador}
          >
            <span className="shell__aba-icone" aria-hidden="true">
              {item.icone}
            </span>
            <span className="shell__aba-rotulo">{item.rotuloMobile}</span>
            {/* O contador fica **fora** do rótulo: o nome da aba continua o literal. */}
            {contador === null ? null : <Contador id={idDoContador} contador={contador} />}
          </Link>
        )
      })}
      <button
        type="button"
        className="shell__aba"
        data-ativo="nao"
        aria-expanded={folhaAberta}
        onClick={aoAlternarFolha}
      >
        <span className="shell__aba-icone" aria-hidden="true">
          {ABA_MAIS.icone}
        </span>
        <span className="shell__aba-rotulo">{ABA_MAIS.rotulo}</span>
      </button>
    </nav>
  )
}

/**
 * A folha "Mais": o resto do menu, o bloco do usuario e "Sair" (NFR-1).
 *
 * Ela e um dialogo de verdade, e nao so um `role="dialog"` decorativo: `aria-modal` diz ao
 * leitor de tela que o resto da pagina esta fora de alcance enquanto ela existe, e o foco
 * **entra** nela na abertura. Sem isso, quem navega por teclado ou leitor abriria a folha e
 * continuaria lendo a tela de tras — a folha existiria so para quem enxerga.
 *
 * A devolucao do foco ao gatilho e do `Shell`, que e quem sabe **qual** dos dois botoes a
 * abriu (a aba "Mais" ou as iniciais do cabecalho).
 */
function Folha({
  itens,
  caminho,
  nome,
  perfilRotulo,
  aoFechar,
  aoSair,
  saindo,
}: {
  itens: ItemDeNavegacao[]
  caminho: string
  nome: string
  perfilRotulo: string
  aoFechar: () => void
  aoSair: () => void
  saindo: boolean
}) {
  const folha = useRef<HTMLDivElement>(null)

  // O foco entra na folha assim que ela existe. `tabIndex={-1}` a torna focavel por codigo
  // sem coloca-la na ordem de tabulacao.
  useEffect(() => {
    folha.current?.focus()
  }, [])

  return (
    <div className="shell__folha-fundo" onClick={aoFechar} role="presentation">
      <div
        ref={folha}
        className="shell__folha"
        role="dialog"
        aria-modal="true"
        aria-label="Mais"
        tabIndex={-1}
        onClick={(evento) => {
          evento.stopPropagation()
        }}
      >
        <nav className="shell__folha-nav" aria-label="Mais itens de navegação">
          {itens.map((item) => (
            <Link
              key={item.chave}
              to={item.caminho}
              className="shell__folha-item"
              data-ativo={item.caminho === caminho ? 'sim' : 'nao'}
              aria-current={item.caminho === caminho ? 'page' : undefined}
              onClick={aoFechar}
            >
              {item.rotulo}
            </Link>
          ))}
        </nav>
        <div className="shell__folha-rodape">
          <BlocoDoUsuario nome={nome} perfilRotulo={perfilRotulo} />
          <button type="button" className="shell__sair" onClick={aoSair} disabled={saindo}>
            Sair
          </button>
        </div>
      </div>
    </div>
  )
}

/** O aviso de versão nova (13.14c, DW-367) — o texto, o botão e o que ele diz sem rede. */
export const TEXTO_DA_VERSAO_NOVA = 'Há uma versão nova do CRM. Salve o que estiver fazendo e recarregue.'
export const RECARREGAR = 'Recarregar'
export const RECARREGANDO = 'Recarregando…'
export const RECARGA_SEM_REDE =
  'Não foi possível buscar a versão nova agora: tente de novo em instantes.'

/**
 * A faixa da versão nova, no topo da área das telas, nas duas árvores. A região viva
 * (`aria-live="polite"`, a semântica de `role="status"` sem o papel: com ele, toda tela teria duas
 * regiões de status, e as consultas por "o status desta tela" achariam a do shell) existe **vazia**
 * desde o carregamento — a que nasce com o texto não é lida pelo leitor de tela —, e
 * o estado mora em `versao-nova.ts` (a virada de 768 px remonta o shell). Sem "fechar": ela só some
 * com a recarga, e nunca recarrega sozinha (quem decide é quem está trabalhando).
 */
function AvisoDeVersaoNova() {
  const versaoNova = useVersaoNova()
  const [recarregando, setRecarregando] = useState(false)
  const [semRede, setSemRede] = useState(false)
  const recarregar = () => {
    setRecarregando(true)
    setSemRede(false)
    void recarregarSemCache().then((recarregou) => {
      if (!recarregou) {
        setRecarregando(false)
        setSemRede(true)
      }
    })
  }
  return (
    <div
      className="shell__versao-nova"
      aria-live="polite"
      aria-atomic="true"
      data-aviso-de-versao=""
      data-aberto={versaoNova ? 'sim' : 'nao'}
    >
      {versaoNova ? (
        <>
          <span className="shell__versao-nova-texto">{TEXTO_DA_VERSAO_NOVA}</span>
          <button
            type="button"
            className="shell__versao-nova-botao"
            disabled={recarregando}
            onClick={recarregar}
          >
            {recarregando ? RECARREGANDO : RECARREGAR}
          </button>
          {semRede ? <span className="shell__versao-nova-erro">{RECARGA_SEM_REDE}</span> : null}
        </>
      ) : null}
    </div>
  )
}

export function Shell() {
  const { sessao, caminho, itens, itemAtual } = useNavegacaoAtual()
  const ehCelular = useEhCelular()
  const navegar = useNavigate()

  /**
   * A folha nao guarda "aberta ou fechada": guarda **em qual rota** ela foi aberta.
   *
   * Assim "trocou de rota, a folha fecha" e derivado, e nao um efeito que reage a navegacao —
   * inclusive quando quem navega e o botao "voltar" do navegador. Um booleano exigiria um
   * `useEffect` sincronizando dois estados que sempre foram um so.
   */
  const [folhaAbertaEm, setFolhaAbertaEm] = useState<string | null>(null)
  const folhaAberta = folhaAbertaEm === caminho

  /**
   * O subtitulo que a tela aberta emprestou, quando ela empresta algum.
   *
   * Ele mora aqui, e nao na tela, porque quem desenha o cabecalho e o `Shell` — nas duas
   * arvores. `setSubtituloDaTela` e o proprio valor do contexto: o setter de `useState` tem
   * identidade estavel, e por isso o efeito de `useDefinirSubtitulo` nao roda a cada render.
   */
  const [subtituloDaTela, setSubtituloDaTela] = useState<string | undefined>(undefined)

  /**
   * O titulo que a tela aberta emprestou, quando ela empresta algum.
   *
   * Ele mora aqui pelo mesmo motivo do subtitulo — quem desenha o cabecalho e o `Shell`, nas
   * duas arvores —, e e o que faz uma tela **sem item de menu** (a ficha do Negocio, a da
   * Empresa) chegar sob um `<h1>` proprio em vez do nome do produto.
   */
  const [tituloDaTela, setTituloDaTela] = useState<string | undefined>(undefined)
  /* O elemento do slot de ações, como estado: quem o consome precisa re-renderizar quando ele
     nasce (o primeiro commit do cabeçalho), e uma ref não avisaria ninguém. */
  const [slotDeAcoes, setSlotDeAcoes] = useState<HTMLElement | null>(null)

  /**
   * Qual botao abriu a folha — a aba "Mais" ou as iniciais do cabecalho.
   *
   * Sem guardar isto, o foco depois de fechar cairia no `body`: quem navega por teclado
   * perderia o lugar e teria de tabular a tela inteira de novo para voltar onde estava.
   */
  const gatilhoDaFolha = useRef<HTMLElement | null>(null)

  const fecharFolha = useCallback(() => {
    setFolhaAbertaEm(null)
    const gatilho = gatilhoDaFolha.current
    gatilhoDaFolha.current = null
    // O gatilho continua montado (ele vive na barra inferior e no cabecalho, nao na folha),
    // entao devolver o foco a ele antes de a folha sair e seguro.
    gatilho?.focus()
  }, [])

  const alternarFolha = (evento: MouseEvent<HTMLButtonElement>) => {
    if (folhaAberta) {
      fecharFolha()
      return
    }
    gatilhoDaFolha.current = evento.currentTarget
    setFolhaAbertaEm(caminho)
  }

  // `Esc` fecha a folha. E o que se espera de qualquer sobreposicao — e, no celular, e a
  // unica saida sem escolher um destino: a folha cobre a barra inferior, inclusive o botao
  // que a abriu.
  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        fecharFolha()
      }
    }
    document.addEventListener('keydown', aoTeclar)
    return () => {
      document.removeEventListener('keydown', aoTeclar)
    }
  }, [fecharFolha])

  const sairDoSistema = useSair(() => {
    void navegar({ to: '/entrar' })
  })

  /* A conferência da versão nova (13.14c): liga com o shell e desliga com ele. Sem assinatura
     (desenvolvimento, teste) ela não pergunta nada. */
  useEffect(() => iniciarAConferencia(), [])

  /*
   * O total de nao lidas do item WhatsApp (13.8, D-8): so de quem tem `acessar_whatsapp` (a claim
   * traz so as permissoes nao negadas — por isso o `in`). A recusa nao desenha nada: o `404` do
   * servico anterior a 13.8, na janela do deploy, vira "sem contador".
   */
  const leWhatsapp = sessao !== undefined && PERMISSAO_DO_WHATSAPP in sessao.permissoes
  const totalDeNaoLidas = useTotalDeNaoLidas({ habilitada: leWhatsapp })
  /* Releitura que falhou não deixa o número de antes no menu como se fosse de agora. */
  const quantasNaoLidas = totalDeNaoLidas.isError ? undefined : totalDeNaoLidas.data?.total
  const contadores: Partial<Record<string, number>> = {
    whatsapp: leWhatsapp && typeof quantasNaoLidas === 'number' ? quantasNaoLidas : undefined,
  }

  // A guarda de rota so entrega o shell com sessao no cache; este `null` cobre o quadro entre
  // o `clear()` do logout e a navegacao para `/entrar`.
  if (!sessao) {
    return null
  }

  // O nome de exibição do perfil da pessoa, como o catálogo o guarda (11.1): "Vendedor sênior", e
  // não o do perfil de fábrica de onde ele deriva.
  const perfilRotulo = sessao.nome_do_perfil
  const aoSair = () => {
    sairDoSistema.mutate()
  }

  const subtituloDoCabecalho = subtituloVigente(itemAtual, subtituloDaTela)
  const tituloDoCabecalho = tituloVigente(itemAtual, tituloDaTela)

  if (ehCelular) {
    return (
      <ContextosDoCabecalho
        subtitulo={setSubtituloDaTela}
        titulo={setTituloDaTela}
        slotDeAcoes={null}
      >
        <div className="shell shell--celular">
          <header className="shell__cabecalho-mobile">
            {/* O mesmo par titulo + subtitulo do computador, empilhado: uma contagem que so o
                computador mostrasse seria informacao perdida no celular, e a NFR-1 nao aceita
                isso. Aqui ele vai **abaixo** do titulo, e nao ao lado, porque a barra tem 46px
                de altura e 16px de folga lateral. */}
            <div className="shell__cabecalho-mobile-texto">
              {/* `<h1>`, e nao `<span>`: sem ele toda tela interna no celular chegaria sem
                  cabecalho nenhum para leitor de tela — justamente na metade da NFR-1 que esta
                  historia existe para atender. O estilo continua o do prototipo. */}
              <h1 className="shell__titulo-mobile">{tituloDoCabecalho}</h1>
              {subtituloDoCabecalho === undefined ? null : (
                <span className="shell__subtitulo-mobile">{subtituloDoCabecalho}</span>
              )}
            </div>
            <button
              type="button"
              className="shell__iniciais-mobile"
              aria-label={`Menu de ${sessao.nome}`}
              aria-expanded={folhaAberta}
              onClick={alternarFolha}
            >
              {iniciaisDoNome(sessao.nome)}
            </button>
          </header>
          <AvisoDeVersaoNova />
          <div className="shell__area">
            <Outlet />
          </div>
          <BarraInferior
            itens={itensDaBarraInferior(itens)}
            caminho={caminho}
            folhaAberta={folhaAberta}
            aoAlternarFolha={alternarFolha}
            contadores={contadores}
          />
          {folhaAberta ? (
            <Folha
              itens={itensDaFolha(itens)}
              caminho={caminho}
              nome={sessao.nome}
              perfilRotulo={perfilRotulo}
              aoFechar={fecharFolha}
              aoSair={aoSair}
              saindo={sairDoSistema.isPending}
            />
          ) : null}
        </div>
      </ContextosDoCabecalho>
    )
  }

  return (
    <ContextosDoCabecalho
      subtitulo={setSubtituloDaTela}
      titulo={setTituloDaTela}
      slotDeAcoes={slotDeAcoes}
    >
      <div className="shell">
        <Sidebar
          itens={itens}
          caminho={caminho}
          nome={sessao.nome}
          perfilRotulo={perfilRotulo}
          aoSair={aoSair}
          saindo={sairDoSistema.isPending}
          contadores={contadores}
        />
        <main className="shell__principal">
          <Cabecalho
            titulo={tituloDoCabecalho}
            subtitulo={subtituloDoCabecalho}
            refDoSlotDeAcoes={setSlotDeAcoes}
          />
          <AvisoDeVersaoNova />
          <div className="shell__area">
            <Outlet />
          </div>
        </main>
      </div>
    </ContextosDoCabecalho>
  )
}
